"use client";

import { useMemo, useState } from "react";
import { Navigation } from "@/components/navigation";
import { Footer } from "@/components/footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plug, Search } from "lucide-react";
import {
  mcpSourceGroups,
  type McpCadenceTone,
  type McpDataset,
  type McpSource,
} from "@/config/mcp-datasets";
import { cn } from "@/lib/utils";

type SourceFilter = "all" | McpSource;

const cadenceDot: Record<McpCadenceTone, string> = {
  muted: "bg-muted-foreground",
  live: "bg-primary",
  daily: "bg-primary/60",
  monthly: "bg-amber-500",
};

const sourceFilters: { value: SourceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "etherscan", label: "Etherscan" },
  { value: "dune", label: "Dune" },
];

function matchesDataset(dataset: McpDataset, query: string) {
  const haystack = `${dataset.name} ${dataset.slug} ${dataset.description} ${
    dataset.paramNote ?? ""
  }`.toLowerCase();
  return haystack.includes(query);
}

function DatasetCard({ dataset }: { dataset: McpDataset }) {
  const Icon = dataset.icon;
  const isDune = dataset.slug.startsWith("dune:");

  return (
    <Card className="flex gap-4 p-5">
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
          isDune
            ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
            : "bg-primary/10 text-primary"
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="font-semibold">{dataset.name}</h3>
          <span className="font-mono text-xs text-muted-foreground">
            {dataset.slug}
          </span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {dataset.description}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                cadenceDot[dataset.cadenceTone]
              )}
            />
            {dataset.cadence}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {dataset.requiredParam ? (
              <>
                <span className="text-foreground">{dataset.requiredParam}</span>
                {dataset.paramNote ? ` ${dataset.paramNote}` : null}
              </>
            ) : (
              "No parameters"
            )}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {dataset.queryOptions}
          </span>
        </div>
      </div>
    </Card>
  );
}

export default function McpPage() {
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mcpSourceGroups
      .filter((group) => source === "all" || group.source === source)
      .map((group) => ({
        ...group,
        visible: group.datasets.filter((dataset) =>
          matchesDataset(dataset, q)
        ),
      }))
      .filter((group) => group.visible.length > 0);
  }, [query, source]);

  const totalVisible = groups.reduce((sum, g) => sum + g.visible.length, 0);
  const isFiltering = query.trim() !== "" || source !== "all";

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section */}
      <section className="border-b border-border/40 py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              MCP Datasets
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
              Protocol fundamentals — ownership, demand analysis, and financials —
              for Sky Ecosystem, Aave, Morpho, and Ethena, served from Etherscan
              chain reads and curated Dune queries through the{" "}
              <span className="font-semibold text-foreground">
                Chain Fundamentals MCP connector
              </span>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section className="py-8 sm:py-12">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {/* Connect card */}
          {/*<Card className="mb-10 p-6 sm:p-8">
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Plug className="h-5 w-5" />
              </span>
              <h2 className="text-xl font-semibold">
                Connect via the Chain Fundamentals MCP connector
              </h2>
            </div>
            <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-6 text-muted-foreground">
              <li>
                <span className="font-semibold text-foreground">
                  Add the connector.
                </span>{" "}
                In Claude, open <code className="font-mono text-xs">Settings → Connectors</code>,
                search for{" "}
                <code className="font-mono text-xs">Chain Fundamentals</code>, and
                connect it to your organization. It authenticates via OAuth —
                there&apos;s no separate API key to manage.
              </li>
              <li>
                <span className="font-semibold text-foreground">
                  Three tools become available in chat.
                </span>{" "}
                <code className="font-mono text-xs">list_datasets()</code>{" "}
                enumerates every dataset with its slug, source, description and
                parameters;{" "}
                <code className="font-mono text-xs">describe_dataset(slug)</code>{" "}
                returns the parameter schema and field list for one dataset;{" "}
                <code className="font-mono text-xs">
                  query_dataset(slug, parameters, start_date, end_date, limit, offset)
                </code>{" "}
                fetches rows as CSV.
              </li>
              <li>
                <span className="font-semibold text-foreground">
                  Just ask.
                </span>{" "}
                You don&apos;t need to call the tools directly — describe what you
                want and Claude picks the right dataset and parameters.
              </li>
            </ol>
            <pre className="mt-5 overflow-x-auto rounded-lg border bg-muted/50 p-4 font-mono text-xs leading-relaxed">
              <span className="text-muted-foreground">
                {"# behind a request like \u201cwhat's sUSDS APY been doing this month?\u201d\n"}
              </span>
              {"query_dataset(\n"}
              {"  slug       = "}
              <span className="text-primary">
                {'"api:savings-token-apy-history"'}
              </span>
              {",\n"}
              {"  parameters = { "}
              <span className="text-primary">{'"symbol"'}</span>
              {": "}
              <span className="text-primary">{'"sUSDS"'}</span>
              {" },\n"}
              {"  start_date = "}
              <span className="text-primary">{'"2026-08-01"'}</span>
              {"\n}"}
            </pre>
            <p className="mt-3 text-sm text-muted-foreground">
              Only datasets marked with a date-range or pagination query option
              (below) accept{" "}
              <code className="font-mono text-xs">start_date</code>/
              <code className="font-mono text-xs">end_date</code> or{" "}
              <code className="font-mono text-xs">limit</code>/
              <code className="font-mono text-xs">offset</code> — pass them and
              they&apos;ll be ignored otherwise.
            </p>
          </Card> */}

          {/* Sticky filter controls */}
          <div className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-b bg-background/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 sm:-mx-6 sm:px-6">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by name, slug, or description…"
                aria-label="Filter datasets"
                className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            <div
              className="flex gap-2"
              role="group"
              aria-label="Filter by source"
            >
              {sourceFilters.map((filter) => (
                <Button
                  key={filter.value}
                  size="sm"
                  variant={source === filter.value ? "default" : "outline"}
                  onClick={() => setSource(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            {isFiltering
              ? `Showing ${totalVisible} dataset${
                  totalVisible === 1 ? "" : "s"
                }.`
              : "Showing all datasets."}
          </p>

          {/* Source groups */}
          {groups.map((group) => (
            <div key={group.source} className="mt-10">
              <div className="flex flex-wrap items-baseline gap-3">
                <h2 className="text-2xl font-semibold">
                  <span
                    className={cn(
                      "mr-3 inline-block rounded px-2 py-0.5 align-middle text-xs font-bold uppercase tracking-wider",
                      group.source === "dune"
                        ? "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                        : "bg-primary text-primary-foreground"
                    )}
                  >
                    {group.label}
                  </span>
                  {group.tagline}
                </h2>
              </div>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                {group.description}
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {group.visible.map((dataset) => (
                  <DatasetCard key={dataset.slug} dataset={dataset} />
                ))}
              </div>
            </div>
          ))}

          {groups.length === 0 && (
            <p className="mt-10 text-center text-sm text-muted-foreground">
              No matching datasets.
            </p>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
